import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Compass, Lock } from "lucide-react";

export const FloatingNavbar = ({ onScrollToSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Nav Links
  const navLinks = [
    { name: "Solutions", id: "solutions" },
    { name: "Industries", id: "industries" },
    { name: "Careers", id: "careers" },
    { name: "About", id: "why" },
    { name: "Contact", id: "contact" }
  ];

  return (
    <>
      <nav
        data-testid="floating-navbar"
        className="fixed top-6 left-1/2 -translate-x-1/2 z-50 rounded-full px-6 py-2.5 backdrop-blur-xl bg-black/60 border border-white/10 flex items-center justify-between gap-12 w-[90%] max-w-6xl transition-all duration-300 shadow-[0_8px_32px_0_rgba(0,0,0,0.5)]"
      >
        {/* Logo */}
        <div
          data-testid="navbar-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center cursor-pointer"
        >
          <img
            src="/logo-clean.png"
            alt="NovaCore Staffing"
            data-testid="navbar-logo-img"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </div>

        {/* Center Links (Desktop) */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => (link.id === "careers" ? navigate("/careers") : onScrollToSection(link.id))}
              className="text-sm font-manrope font-medium text-slate-300 hover:text-white transition-colors duration-200"
            >
              {link.name}
            </button>
          ))}
          <button
            data-testid="nav-link-admin"
            onClick={() => navigate("/admin/login")}
            className="text-sm font-manrope font-medium text-slate-300 hover:text-[#65FFB6] transition-colors duration-200 flex items-center gap-1"
          >
            <Lock className="w-3 h-3" /> Admin
          </button>
        </div>

        {/* Right Buttons */}
        <div className="hidden lg:flex items-center gap-4">
          <Button
            data-testid="nav-explore-careers-btn"
            variant="outline"
            size="sm"
            onClick={() => navigate("/careers")}
            className="border-white/10 text-white hover:bg-white/10 hover:text-white rounded-full font-manrope font-medium text-xs flex items-center gap-1.5 py-1 h-8"
          >
            <Compass className="w-3.5 h-3.5" />
            Explore Careers
          </Button>

          <Button
            data-testid="nav-hire-talent-btn"
            onClick={() => onScrollToSection("contact")}
            className="bg-[#65FFB6] hover:bg-[#A4FFD6] text-[#05080A] rounded-full font-outfit font-semibold px-5 h-9 text-xs tracking-wide shadow-[0_0_15px_rgba(101,255,182,0.4)] transition-all duration-300 hover:scale-[1.03]"
          >
            Hire Talent
          </Button>
        </div>

        {/* Mobile Hamburger Trigger */}
        <button
          data-testid="mobile-menu-trigger"
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden text-slate-300 hover:text-white transition-colors p-1"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Fullscreen Menu */}
      {isOpen && (
        <div
          data-testid="mobile-menu-overlay"
          className="fixed inset-0 z-40 bg-slate-950/98 backdrop-blur-2xl flex flex-col justify-center items-center gap-8 lg:hidden animate-fade-in"
        >
          <div className="flex flex-col items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`mobile-nav-link-${link.id}`}
                onClick={() => {
                  setIsOpen(false);
                  if (link.id === "careers") navigate("/careers");
                  else onScrollToSection(link.id);
                }}
                className="text-2xl font-outfit font-medium text-slate-300 hover:text-[#65FFB6] transition-colors"
              >
                {link.name}
              </button>
            ))}
            <button
              data-testid="mobile-nav-link-admin"
              onClick={() => { setIsOpen(false); navigate("/admin/login"); }}
              className="text-2xl font-outfit font-medium text-slate-300 hover:text-[#65FFB6] transition-colors flex items-center gap-2"
            >
              <Lock className="w-4 h-4" /> Admin
            </button>
          </div>

          <div className="w-[1px] h-12 bg-white/10"></div>

          <div className="flex flex-col items-center gap-4 w-full px-12">
            <Button
              data-testid="mobile-explore-careers-btn"
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                navigate("/careers");
              }}
              className="w-48 border-white/10 text-white rounded-full"
            >
              Explore Careers
            </Button>

            <Button
              data-testid="mobile-hire-btn"
              onClick={() => {
                setIsOpen(false);
                onScrollToSection("contact");
              }}
              className="w-48 bg-[#65FFB6] hover:bg-[#A4FFD6] text-black rounded-full font-semibold"
            >
              Hire Elite Talent
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
